/**
 * admin-tab-meta — słownik etykiet + ikon kart dla powłoki ADMINA (panel ITE).
 *
 * Etykiety bierzemy wprost z `ADMIN_MENU` (config/menu.ts) — spójnie z sidebarem admina
 * (admin menu jest statyczne, nie i18n). Dla tras szczegółów / podstron (np. `/tenants/:id`,
 * `/bundles/:code`, kreatory szablonów) mapujemy ręcznie etykietę „pojedynczą".
 */

import { ADMIN_MENU } from '@/config/menu';
import type { TabMeta } from '@/contexts/TabContext';

export const ADMIN_TABS_STORAGE_KEY = 'saas_admin_open_tabs';
export const ADMIN_DEFAULT_PATH = '/dashboard';

/** Pełna ścieżka → {label, icon} z menu admina. */
const BY_PATH: Record<string, TabMeta> = Object.fromEntries(
  ADMIN_MENU.map((m) => [m.path, { label: m.label, icon: m.icon }]),
);

/** Trasy spoza menu (mają osobny widok, ale nie ma ich w sidebarze). */
const EXTRA: Record<string, TabMeta> = {
  '/admin-roles': { label: 'Role', icon: 'ShieldCheck' },
  '/profile': { label: 'Profil', icon: 'UserCog' },
};

/** Etykieta „pojedyncza" dla tras szczegółów — wg pierwszego segmentu. */
const DETAIL: Record<string, TabMeta> = {
  tenants: { label: 'Tenant', icon: 'Building2' },
  bundles: { label: 'Pakiet', icon: 'Package' },
  'module-templates': { label: 'Szablon modułu', icon: 'LayoutTemplate' },
};

const DEFAULT_ICON = 'File';

export function resolveAdminTabMeta(path: string): TabMeta {
  const clean = (path.split('?')[0] || '/').replace(/\/+$/, '') || '/';
  const segments = clean.split('/').filter(Boolean);

  const first = segments[0];
  if (!first) return BY_PATH['/dashboard'] ?? { label: 'Dashboard', icon: 'LayoutDashboard' };

  const full = '/' + segments.join('/');
  const exact = BY_PATH[full] ?? EXTRA[full];
  if (exact) return exact;

  // Podstrony / szczegóły.
  if (segments.length > 1) {
    if (segments.includes('form-builder')) {
      return { label: 'Kreator formularza', icon: 'LayoutTemplate' };
    }
    if (segments.includes('list-builder')) {
      return { label: 'Kreator listy', icon: 'LayoutTemplate' };
    }
    // Edytor szablonu modułu — dorzuć identyfikator szablonu, żeby odróżnić karty
    // (kilka otwartych „Szablon modułu" było nieodróżnialnych). Kod ze ścieżki to
    // fallback; strona edytora podmienia go na nazwę szablonu po wczytaniu.
    if (first === 'module-templates') {
      const code = segments[1];
      return {
        label: code ? `Szablon modułu — ${code}` : 'Szablon modułu',
        icon: 'LayoutTemplate',
      };
    }
    // Przeglądarka bazy tenanta — strona podmienia etykietę na „Baza: <nazwa>" po wczytaniu.
    if (first === 'tenants' && segments[2] === 'database') {
      return { label: 'Baza tenanta', icon: 'Database' };
    }
    const detail = DETAIL[first];
    if (detail) return detail;
  }

  const root = BY_PATH['/' + first];
  if (root) return root;

  return { label: first.charAt(0).toUpperCase() + first.slice(1), icon: DEFAULT_ICON };
}
